import { makeT, esc } from "../i18n";

const t = makeT("cart");

/*
 * Cobuntu Cart Widget v1
 *
 * Drop-in cart UI for white-label community landing pages. Renders a
 * cart icon + drawer + checkout flow in vanilla DOM. Reads/writes the
 * SAME localStorage key (`cobuntu_community_cart`) the community-app's
 * React CartContext uses — so a user adding a product on /marketplace
 * sees the badge update on / and vice versa.
 *
 * Contract: see docs/features/cobuntu-cart-widget.md. v1 is frozen
 * once shipped to a customer in production. Public-API discipline.
 */

// ─── Types — must match community-app/lib/cart-context.tsx CartItem ──

interface CartItem {
  id: string;
  type: "EVENT" | "PRODUCT";
  sku: string;
  productId: string;
  name: string;
  price: number; // cents (smallest currency unit) - the UNIT price
  currency: string;
  /**
   * Units of this line. Added to the shared localStorage contract by T-142.
   *
   * Optional because carts written before T-142 are still in visitors'
   * browsers and have no such field - `?? 1` everywhere it is read, never a
   * bare `item.quantity`.
   */
  quantity?: number;
  /**
   * The tier the buyer picked, as its OWN field.
   *
   * The app separated this from `name` so the drawer could style it and tell
   * it apart from a product whose real name simply contains " - ". The widget
   * never learned, so an embedded cart still showed "Workshop - Early bird"
   * glued into one line while the app showed two.
   */
  tierName?: string;
  imageUrl?: string;
  eventId?: string;
  eventDate?: string;
  eventLocation?: string;
  communityListingId?: string;
  addedAt: string;
}

interface WidgetConfig {
  communityTag: string;
  publishableKey: string;
  apiUrl: string;
}

// ─── Constants ────────────────────────────────────────────────────

const STORAGE_KEY = "cobuntu_community_cart";
const CONFIG_CACHE_KEY = "cobuntu_widget_config_v1";
const CONFIG_TTL_MS = 60 * 60 * 1000;
const MOUNT_SELECTOR = "[data-cobuntu-cart]";
const MOUNTED_ATTR = "data-cobuntu-cart-mounted";
const STYLE_ID = "cobuntu-cart-widget-styles";

import { authedFetch } from "../api/authedFetch";
import { getCachedSession, primeSession } from "../shared/session";
import { appNav, widgetOrigin } from "../shared/origin";
import {
  isCrossOrigin,
  configureServerCart,
  primeServerCart,
  snapshot,
  removeItem as storeRemoveItem,
  setQuantity as storeSetQuantity,
  handoffUrl,
  writeLocalCart,
} from "../cart/store";
import { fetchQuote, type Quote, type QuoteRefusal } from "../cart/quote";

declare const __WIDGET_API_URL__: string;

/**
 * Absolute API base for UNAUTHENTICATED calls only — community lookup by
 * domain, publishable key. Those endpoints carry no session, so cross-origin
 * is fine and the widget may run on a customer landing where they are not
 * proxied.
 *
 * AUTHED calls must NOT use this. They go through `authedFetch`, which is
 * same-origin by design: the cobuntu_auth cookie is host-scoped, so an authed
 * cross-origin call arrives anonymous and every widget renders a guest. That
 * was a live outage — see api/authedFetch.ts.
 */
const API_URL: string =
  typeof __WIDGET_API_URL__ !== "undefined" ? __WIDGET_API_URL__ : "https://api.cobuntu.com";



// ─── State ────────────────────────────────────────────────────────

let configPromise: Promise<WidgetConfig> | null = null;
let drawerEl: HTMLElement | null = null;
/** Server-priced totals for the cart currently in the drawer. */
let quote: Quote | null = null;
let quoteRefusal: QuoteRefusal | null = null;
let quoteSeq = 0;
let backdropEl: HTMLElement | null = null;

// ─── Cart store (localStorage, shared with community-app) ─────────

/**
 * The cart, synchronously.
 *
 * Same-origin this is localStorage, exactly as before. Cross-origin it is the
 * server cart's cached snapshot — localStorage is partitioned by origin, so on
 * a decoupled landing the local store is a different store from the app's and
 * the widget was rendering a cart nothing could fill. See cartStore.ts.
 */
function readCart(): CartItem[] {
  return snapshot() as CartItem[];
}

/**
 * Replace the whole cart. SAME-ORIGIN ONLY — the server cart is mutated
 * through its own endpoints (add / set-quantity / remove), never by writing an
 * array back, because the server owns line ids and quantities and a wholesale
 * overwrite would let a stale tab clobber another's changes.
 */
function writeCart(items: CartItem[]): void {
  if (isCrossOrigin()) return;
  // Quota errors are swallowed here and surfaced by the drawer's error region,
  // as before.
  writeLocalCart(items as any);
}

/** Units on one line, tolerating pre-T-142 carts that carry no quantity. */
function unitsOf(item: CartItem): number {
  const q = item.quantity ?? 1;
  return Number.isFinite(q) && q > 0 ? Math.floor(q) : 1;
}

/**
 * Total UNITS in the cart, not rows.
 *
 * This counted rows, so a cart holding 3 of one ticket showed "1" - and
 * disagreed with the community app's own badge for the very same cart.
 */
function cartCount(): number {
  return readCart().reduce((n, i) => n + unitsOf(i), 0);
}

// ─── Config bootstrap (by-domain → tag → publishable key) ─────────

function loadConfig(): Promise<WidgetConfig> {
  if (configPromise) return configPromise;

  const host = location.host.split(":")[0];

  // Session cache to avoid two extra fetches on every page nav.
  try {
    const raw = sessionStorage.getItem(CONFIG_CACHE_KEY);
    if (raw) {
      const cached = JSON.parse(raw) as { host: string; ts: number; config: WidgetConfig };
      if (cached.host === host && Date.now() - cached.ts < CONFIG_TTL_MS) {
        configPromise = Promise.resolve(cached.config);
        return configPromise;
      }
    }
  } catch {
    /* ignore parse / quota errors — fall through to network */
  }

  configPromise = (async () => {
    const domainRes = await fetch(
      `${API_URL}/api/communities/by-domain/${encodeURIComponent(host)}`,
      { credentials: "omit" },
    );
    if (!domainRes.ok) {
      throw new Error(`Widget: community not found for host ${host}`);
    }
    const community = (await domainRes.json()) as { communityTag?: string };
    const tag = (community.communityTag || "").toLowerCase();
    if (!tag) throw new Error("Widget: missing communityTag in by-domain response");

    const pkRes = await fetch(
      `${API_URL}/api/communities/${encodeURIComponent(tag)}/publishable-key`,
      { credentials: "omit" },
    );
    if (!pkRes.ok) throw new Error(`Widget: publishable-key fetch failed (${pkRes.status})`);
    const { key } = (await pkRes.json()) as { key: string };
    if (!key) throw new Error("Widget: publishable-key response missing `key`");

    const config: WidgetConfig = { communityTag: tag, publishableKey: key, apiUrl: API_URL };
    try {
      sessionStorage.setItem(
        CONFIG_CACHE_KEY,
        JSON.stringify({ host, ts: Date.now(), config }),
      );
    } catch { /* ignore */ }
    return config;
  })();

  // Drop the cached promise on rejection so retries can succeed.
  configPromise.catch(() => { configPromise = null; });
  return configPromise;
}

// ─── Auth (same-origin cookie reader, mirrors community-app/lib/api.ts) ─

/**
 * Is this visitor signed in? (app platform S3.)
 *
 * Was a cookie read. S5 makes the cookie httpOnly, at which point the read
 * returns nothing and every shopper — signed in or not — gets bounced to the
 * login page at checkout. Asks the server-resolved session instead.
 */
function isAuthenticated(): boolean {
  return getCachedSession()?.authenticated === true;
}

// ─── Trigger (icon + badge) ───────────────────────────────────────

function renderTrigger(host: HTMLElement): void {
  host.setAttribute(MOUNTED_ATTR, "");
  host.innerHTML = "";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "cobuntu-cart-trigger";
  btn.setAttribute("aria-label", "Open cart");
  btn.innerHTML = `
    <svg class="cobuntu-cart-trigger__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="9" cy="21" r="1"></circle>
      <circle cx="20" cy="21" r="1"></circle>
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
    </svg>
    <span class="cobuntu-cart-trigger__badge" hidden>0</span>
  `;
  // Click opens the widget's own slide-out cart DRAWER (matches the
  // community-app mobile cart). It briefly navigated to a full-page
  // /cart route (2026-05-25), but that breaks on customer landings:
  // /cart hits the community-app proxy, which mobile-redirects to
  // `/?cart=open` — a param only the community-app's MobileTopBar
  // handles, so the landing just bounces back with nothing open. The
  // self-contained drawer works on every host (landing or app, any
  // viewport) with no navigation.
  btn.addEventListener("click", handleTriggerClick);
  host.appendChild(btn);
  updateBadgeOn(host);
}

function handleTriggerClick(e: MouseEvent): void {
  // preventDefault + stopPropagation so the click doesn't bubble into
  // the customer's landing router (SPA landings like Lovable intercept
  // clicks) before we open the drawer.
  e.preventDefault();
  openDrawer();
}

function updateBadgeOn(host: HTMLElement): void {
  const badge = host.querySelector<HTMLElement>(".cobuntu-cart-trigger__badge");
  if (!badge) return;
  const count = cartCount();
  badge.textContent = count > 99 ? "99+" : String(count);
  if (count === 0) badge.setAttribute("hidden", "");
  else badge.removeAttribute("hidden");
}

function updateAllBadges(): void {
  document
    .querySelectorAll<HTMLElement>(MOUNT_SELECTOR)
    .forEach(updateBadgeOn);
}

// ─── Drawer ───────────────────────────────────────────────────────

function ensureDrawer(): void {
  if (drawerEl && backdropEl) return;

  backdropEl = document.createElement("div");
  backdropEl.className = "cobuntu-cart-backdrop";
  backdropEl.addEventListener("click", closeDrawer);

  drawerEl = document.createElement("aside");
  drawerEl.className = "cobuntu-cart-drawer";
  drawerEl.setAttribute("role", "dialog");
  drawerEl.setAttribute("aria-modal", "true");
  drawerEl.setAttribute("aria-label", "Shopping cart");
  drawerEl.tabIndex = -1;

  document.body.appendChild(backdropEl);
  document.body.appendChild(drawerEl);

  // ESC to close
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawerEl?.classList.contains("cobuntu-cart-drawer--open")) {
      closeDrawer();
    }
  });
}

function openDrawer(): void {
  ensureDrawer();
  renderDrawer();
  requestAnimationFrame(() => {
    backdropEl!.classList.add("cobuntu-cart-backdrop--open");
    drawerEl!.classList.add("cobuntu-cart-drawer--open");
    drawerEl!.focus();
  });
  // Pre-warm the config fetch so checkout is instant when clicked.
  loadConfig().catch(() => { /* surfaced on checkout click */ });
  // Price it. The drawer paints immediately from the local sum and upgrades to
  // the server's figure when it lands, so opening the cart is never blocked on
  // a network call.
  void refreshQuote();
}

function closeDrawer(): void {
  if (!drawerEl || !backdropEl) return;
  backdropEl.classList.remove("cobuntu-cart-backdrop--open");
  drawerEl.classList.remove("cobuntu-cart-drawer--open");
}

/**
 * Re-price the cart against the server and re-render.
 *
 * Sequenced, because the cart is edited fast enough (remove, undo, remove
 * again) for replies to race — the same guard the React hook uses. A LATE
 * reply for a cart that no longer exists must not overwrite a newer total.
 *
 * A failed call clears the quote and falls back to the local sum rather than
 * showing nothing: an unpriced cart is still a cart, and "total unavailable"
 * on a drawer someone is about to check out from is worse than a figure that
 * is right in the common case.
 */
async function refreshQuote(): Promise<void> {
  const items = readCart();
  if (items.length === 0) { quote = null; quoteRefusal = null; return; }

  const seq = ++quoteSeq;
  try {
    const config = await loadConfig();
    const res = await fetchQuote(
      API_URL,
      config.communityTag,
      items.map((i) => ({
        // The line's productId is already the id checkout keys on: the server
        // cart resolves a variant to its own backing product, and the app's
        // cart stores the same thing.
        productId: i.productId,
        quantity: unitsOf(i),
      })),
      { sameOrigin: !isCrossOrigin() },
    );
    if (seq !== quoteSeq) return;
    quote = res?.quote ?? null;
    quoteRefusal = res?.refusal ?? null;
  } catch {
    if (seq !== quoteSeq) return;
    quote = null;
    quoteRefusal = null;
  }
  if (drawerEl?.classList.contains("cobuntu-cart-drawer--open")) renderDrawer();
}

function renderDrawer(): void {
  if (!drawerEl) return;
  const items = readCart();
  const currency = items[0]?.currency || "USD";
  // price is the UNIT price; summing it raw under-stated every multi-unit
  // cart, which is the same arithmetic the checkout body got wrong below.
  const subtotal = items.reduce((s, i) => s + i.price * unitsOf(i), 0);
  const unitCount = items.reduce((n, i) => n + unitsOf(i), 0);

  drawerEl.innerHTML = `
    <header class="cobuntu-cart-drawer__header">
      <div class="cobuntu-cart-drawer__title">
        <span>${esc(t("cart", "Cart"))}</span>
        ${items.length > 0
          ? `<span class="cobuntu-cart-drawer__count">${unitCount}</span>`
          : ""}
      </div>
      <button type="button" class="cobuntu-cart-drawer__close" aria-label="${esc(t("closeCart", "Close cart"))}">
        <span class="cobuntu-cart-drawer__close-line cobuntu-cart-drawer__close-line--top"></span>
        <span class="cobuntu-cart-drawer__close-line cobuntu-cart-drawer__close-line--bottom"></span>
      </button>
    </header>
    <div class="cobuntu-cart-drawer__list" role="list">
      ${items.length === 0
        ? `<div class="cobuntu-cart-empty">
            <div class="cobuntu-cart-empty__icon" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"></path>
              </svg>
            </div>
            <p class="cobuntu-cart-empty__title">${esc(t("emptyTitle", "Your cart is empty"))}</p>
            <p class="cobuntu-cart-empty__hint">Browse products and events to add items to your cart.</p>
            <div class="cobuntu-cart-empty__actions">
              <a class="cobuntu-cart-empty__btn cobuntu-cart-empty__btn--primary" href="/marketplace">${esc(t("goToMarketplace", "Go to Marketplace"))}</a>
              <a class="cobuntu-cart-empty__btn cobuntu-cart-empty__btn--muted" href="/events">${esc(t("goToEvents", "Go to Events"))}</a>
            </div>
          </div>`
        : items.map(itemHtml).join("")}
    </div>
    ${items.length > 0
      ? `
        <footer class="cobuntu-cart-footer">
          ${quote
            ? `${quote.memberSavings > 0
                  ? `<div class="cobuntu-cart-footer__row cobuntu-cart-footer__row--muted">
                       <span class="cobuntu-cart-footer__row-label">${esc(t("memberSavings", "Member savings"))}</span>
                       <span class="cobuntu-cart-footer__row-save">-${formatPrice(quote.memberSavings, quote.currency)}</span>
                     </div>`
                  : ""}
                ${quote.requiresShipping || quote.shipping > 0
                  ? `<div class="cobuntu-cart-footer__row cobuntu-cart-footer__row--muted">
                       <span class="cobuntu-cart-footer__row-label">${esc(t("shipping", "Shipping"))}</span>
                       <span>${quote.shipping > 0 ? formatPrice(quote.shipping, quote.currency) : t("calculatedAtCheckout", "Calculated at checkout")}</span>
                     </div>`
                  : ""}
                <div class="cobuntu-cart-footer__row">
                  <span class="cobuntu-cart-footer__row-label">${esc(t("total", "Total"))}</span>
                  <span class="cobuntu-cart-footer__row-value">${formatPrice(quote.total, quote.currency)}</span>
                </div>`
            : `<div class="cobuntu-cart-footer__row">
                 <span class="cobuntu-cart-footer__row-label">${esc(t("total", "Total"))}</span>
                 <span class="cobuntu-cart-footer__row-value">${formatPrice(subtotal, currency)}</span>
               </div>`}
          ${quoteRefusal
            ? `<p class="cobuntu-cart-refusal">${escapeHtml(quoteRefusal.message)}</p>`
            : ""}
          <button type="button" class="cobuntu-cart-checkout"${quoteRefusal ? " disabled" : ""}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2"></rect>
              <path d="M7 11V7a5 5 0 0110 0v4"></path>
            </svg>
            <span class="cobuntu-cart-checkout__label">${esc(t("checkout", "Checkout"))}</span>
          </button>
          <p class="cobuntu-cart-error" hidden></p>
          ${stripeBadgeHtml()}
        </footer>
      `
      : ""}
  `;

  drawerEl
    .querySelector<HTMLButtonElement>(".cobuntu-cart-drawer__close")
    ?.addEventListener("click", closeDrawer);

  drawerEl
    .querySelectorAll<HTMLButtonElement>(".cobuntu-cart-qty__btn")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.qtyItem!;
        const next = Number(btn.dataset.qty);
        // Disable the whole group while the write is in flight, so a double
        // click cannot queue two increments against a stale count.
        drawerEl?.querySelectorAll<HTMLButtonElement>(".cobuntu-cart-qty__btn")
          .forEach((b) => { b.disabled = true; });
        void storeSetQuantity(id, next).then(() => {
          renderDrawer();
          updateAllBadges();
          void refreshQuote();
        });
      });
    });

  drawerEl
    .querySelectorAll<HTMLButtonElement>(".cobuntu-cart-item__remove")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.itemId!;
        // Async for the server cart, immediate for localStorage; both end in
        // the same re-render so the two backends look identical here.
        void storeRemoveItem(id).then(() => {
          renderDrawer();
          updateAllBadges();
          // Totals change with the cart; a stale quote would show the price of
          // an order the buyer no longer has.
          void refreshQuote();
        });
      });
    });

  drawerEl
    .querySelector<HTMLButtonElement>(".cobuntu-cart-checkout")
    ?.addEventListener("click", handleCheckout);
}

/*
 * The banner fallback, mirroring @cobuntu/management-ui-shared.
 *
 * DUPLICATED ON PURPOSE, and that is uncomfortable: the shared package exists
 * because this artwork had drifted into three copies that stopped agreeing.
 * The widget is a standalone bundle for third-party sites and cannot import
 * app code, so a fourth copy is the only way it can draw the same thing.
 *
 * What stops it drifting is a TEST, not discipline: widget-cart-parity asserts
 * this produces byte-identical output to the package's own
 * bannerPlaceholderGradient across a spread of seeds. Change one without the
 * other and it goes red.
 *
 * The empty grey div this replaces was the exact failure the package's own
 * comment describes - image-less items reading as a loading error rather than
 * as artwork.
 */
const PLACEHOLDER_PALETTES = [
  { angle: 135, from: "#c87038", via: "#e09050", to: "#eda870" },
  { angle: 145, from: "#1080c0", via: "#20a4f3", to: "#55c0f5" },
  { angle: 125, from: "#3a7050", via: "#4a8a68", to: "#65a880" },
  { angle: 155, from: "#6840a0", via: "#8060c0", to: "#9a80d8" },
  { angle: 135, from: "#a03828", via: "#c05040", to: "#d07060" },
  { angle: 130, from: "#3a4858", via: "#4a5a6a", to: "#6a7a8a" },
  { angle: 140, from: "#50685a", via: "#688070", to: "#84a090" },
  { angle: 150, from: "#805830", via: "#a07040", to: "#c09060" },
];

const PLACEHOLDER_OVERLAYS = [
  ["linear-gradient(158deg, transparent 42%, rgba(255,255,255,0.12) 42%, rgba(255,255,255,0.12) 58%, transparent 58%)", "linear-gradient(148deg, transparent 62%, rgba(0,0,0,0.08) 62%, rgba(0,0,0,0.08) 80%, transparent 80%)"],
  ["linear-gradient(125deg, transparent 25%, rgba(255,255,255,0.10) 25%, rgba(255,255,255,0.10) 40%, transparent 40%)", "linear-gradient(170deg, transparent 55%, rgba(0,0,0,0.09) 55%, rgba(0,0,0,0.09) 72%, transparent 72%)"],
  ["linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.08) 30%, rgba(255,255,255,0.08) 50%, transparent 50%)", "linear-gradient(160deg, transparent 50%, rgba(0,0,0,0.06) 50%, rgba(0,0,0,0.06) 70%, transparent 70%)"],
  ["linear-gradient(145deg, transparent 20%, rgba(255,255,255,0.07) 20%, rgba(255,255,255,0.07) 35%, transparent 35%)", "linear-gradient(155deg, transparent 60%, rgba(0,0,0,0.07) 60%, rgba(0,0,0,0.07) 75%, transparent 75%)"],
  ["linear-gradient(140deg, transparent 35%, rgba(255,255,255,0.09) 35%, rgba(255,255,255,0.09) 48%, transparent 48%)", "linear-gradient(165deg, transparent 45%, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0.05) 65%, transparent 65%)"],
];

/** Must stay identical to the package's hash: the same listing has to keep
 *  the same artwork on every surface. */
export function placeholderHash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return h >>> 0;
}

export function placeholderGradient(seed: string): string {
  const hash = placeholderHash(seed || "listing");
  const p = PLACEHOLDER_PALETTES[hash % PLACEHOLDER_PALETTES.length]!;
  const o = PLACEHOLDER_OVERLAYS[(hash >>> 3) % PLACEHOLDER_OVERLAYS.length]!;
  const base = `linear-gradient(${p.angle}deg, ${p.from} 0%, ${p.via} 50%, ${p.to} 100%)`;
  return [...o, base].join(", ");
}

function itemHtml(item: CartItem): string {
  const imgHtml = item.imageUrl
    ? `<img class="cobuntu-cart-item__image" src="${escapeAttr(item.imageUrl)}" alt="">`
    : `<div class="cobuntu-cart-item__image cobuntu-cart-item__image--placeholder" style="background-image:${escapeAttr(placeholderGradient(item.sku || item.productId || item.id))}"></div>`;
  const metaParts: string[] = [];
  if (item.type === "EVENT" && item.eventDate) metaParts.push(formatDate(item.eventDate));
  if (item.type === "EVENT" && item.eventLocation) metaParts.push(item.eventLocation);
  const metaHtml = metaParts.length
    ? `<div class="cobuntu-cart-item__meta">${escapeHtml(metaParts.join(" · "))}</div>`
    : "";
  /*
   * A multi-unit line states its arithmetic. Showing the unit price alone
   * beside a total that had multiplied it would read as a pricing error.
   */
  const units = unitsOf(item);
  const qtyHtml = units > 1
    ? `${formatPrice(item.price, item.currency)} × ${units}`
      + ` <strong>${formatPrice(item.price * units, item.currency)}</strong>`
    : formatPrice(item.price, item.currency);

  /*
   * Quantity stepper — server cart only.
   *
   * The localStorage cart has no quantity endpoint to call and its own client
   * rejects duplicates by sku, so a stepper there would be a control with
   * nothing behind it. Cross-origin the server owns the quantity, so this is
   * the only place a buyer can say "two of those" without pressing Add twice.
   */
  const stepperHtml = isCrossOrigin()
    ? `<div class="cobuntu-cart-qty" role="group" aria-label="${esc(t("quantity", "Quantity"))}">
        <button type="button" class="cobuntu-cart-qty__btn" data-qty-item="${escapeAttr(item.id)}" data-qty="${units - 1}" aria-label="${esc(t("decreaseQuantity", "Decrease quantity"))}">-</button>
        <span class="cobuntu-cart-qty__value" aria-live="polite">${units}</span>
        <button type="button" class="cobuntu-cart-qty__btn" data-qty-item="${escapeAttr(item.id)}" data-qty="${units + 1}" aria-label="${esc(t("increaseQuantity", "Increase quantity"))}">+</button>
      </div>`
    : "";

  return `
    <div class="cobuntu-cart-item" role="listitem">
      ${imgHtml}
      <div class="cobuntu-cart-item__body">
        <div class="cobuntu-cart-item__name">${escapeHtml(item.name)}</div>
        ${item.tierName ? `<div class="cobuntu-cart-item__tier">${escapeHtml(item.tierName)}</div>` : ""}
        ${metaHtml}
        <div class="cobuntu-cart-item__price">${qtyHtml}</div>
        ${stepperHtml}
      </div>
      <button type="button" class="cobuntu-cart-item__remove" data-item-id="${escapeAttr(item.id)}" aria-label="${esc(t("remove", "Remove"))}" title="Remove">
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6"></path>
          <path d="M10 11v6M14 11v6"></path>
        </svg>
      </button>
    </div>
  `;
}

// ─── Checkout ─────────────────────────────────────────────────────

async function handleCheckout(): Promise<void> {
  if (!drawerEl) return;
  const btn = drawerEl.querySelector<HTMLButtonElement>(".cobuntu-cart-checkout");
  const errEl = drawerEl.querySelector<HTMLElement>(".cobuntu-cart-error");
  if (!btn || !errEl) return;

  // Swap only the label span, never the button's textContent — the button also
  // holds the lock icon SVG, which a textContent write would wipe.
  const labelEl = btn.querySelector<HTMLElement>(".cobuntu-cart-checkout__label") ?? btn;
  errEl.hidden = true;
  btn.disabled = true;
  const originalLabel = labelEl.textContent || t("checkout", "Checkout");
  labelEl.textContent = "Redirecting…";

  try {
    const items = readCart();
    if (items.length === 0) return;

    const config = await loadConfig();

    /*
     * CROSS-ORIGIN: hand the cart to the app and let its review step take it
     * from here. The landing never posts a checkout itself — the review step,
     * the quote, the refund copy and the postage handling all live on the app,
     * and reimplementing them on a landing page is how two checkouts drift.
     *
     * What travels is a ONE-TIME 60s code, never the cart token: the token is
     * long-lived, and a URL leaks into history, Referer and any log that
     * records query strings.
     */
    if (isCrossOrigin()) {
      location.href = await handoffUrl(widgetOrigin());
      return;
    }

    // The session is resolved before checkout can be clicked; prime defensively
    // in case this path is reached before any widget mounted.
    await primeSession();
    if (!isAuthenticated()) {
      // Unauthenticated — bounce to the white-label login, come back to
      // marketplace where the community-app can resume the checkout.
      location.href = appNav(`/login?next=${encodeURIComponent("/marketplace")}`);
      return;
    }

    // v0.5.0 — same-origin relative path. config.apiUrl is left
    // populated for cart-specific public calls (publishable-key,
    // by-domain) that are CORS-OK cross-origin; the authed checkout
    // POST goes through the customer apex proxy so the visitor's
    // apex cookie reaches the BE server-side.
    const res = await authedFetch(`/api/users/me/checkout`, {
      method: "POST",
      // No authToken: same-origin, so the browser attaches the cookie and the
      // apex proxy forwards it server-side. Works under httpOnly.
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": config.publishableKey,
      },
      body: JSON.stringify({
        items: items.map((i) => ({
          productId: i.productId,
          communityListingId: i.communityListingId || undefined,
          /*
           * Was hardcoded to 1. A buyer could set quantity 3 in the community
           * app, check out from an embedded widget, and be charged for one -
           * the widget shares the cart's localStorage but ignored the field.
           */
          quantity: unitsOf(i),
        })),
        checkoutCurrency: items[0].currency,
        communityTag: config.communityTag,
        origin: location.origin,
      }),
    });

    if (!res.ok) {
      let msg = `Checkout failed (${res.status})`;
      try {
        const body = await res.json();
        if (body?.error) msg = body.error;
        else if (body?.message) msg = body.message;
      } catch { /* no JSON body */ }
      throw new Error(msg);
    }
    const data = (await res.json()) as { checkoutUrl?: string };
    if (!data.checkoutUrl) throw new Error("Missing checkout URL");
    location.href = data.checkoutUrl;
  } catch (e) {
    const msg = e instanceof Error ? e.message : t("checkoutFailed", "Checkout failed");
    errEl.textContent = msg;
    errEl.hidden = false;
    btn.disabled = false;
    labelEl.textContent = originalLabel;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default: return "&#39;";
    }
  });
}
const escapeAttr = escapeHtml;

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Currencies whose smallest unit IS the major unit, per Stripe's zero-decimal
 * list. Dividing these by 100 understates the price 100x (¥1000 -> "¥10").
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  "BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA",
  "PYG", "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF",
]);

/**
 * Mirrors the app's lib/money.ts `formatMoney`.
 *
 * Deliberately duplicated rather than imported: the widget ships as a
 * standalone bundle for third-party sites and must not pull in app code. Keep
 * the two in step — lib/money.ts is the reference implementation.
 *
 * Unlike the app it leaves the locale undefined, so an embedded cart formats
 * in the host page visitor's own locale.
 */
function formatPrice(price: number, currency: string): string {
  if (!price) return t("free", "Free");
  const code = (currency || "EUR").toUpperCase();
  const zeroDecimal = ZERO_DECIMAL_CURRENCIES.has(code);
  const value = zeroDecimal ? price : price / 100;
  try {
    // Fraction digits are left to Intl, which uses each currency's own minor
    // unit count. Forcing a 0 minimum rendered "€10" beside "€10.50".
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(value);
  } catch {
    return `${value.toFixed(zeroDecimal ? 0 : 2)} ${code}`;
  }
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return s;
  }
}

/**
 * Trust badge shown in the drawer footer above (visually below) the
 * Checkout button. Communicates that card data is handled by Stripe
 * (PCI-DSS Level 1) so the visitor knows their card never touches
 * Cobuntu directly. Mirrors the StripeBadge React component used on
 * the community-app's /cart page and mobile CartDrawer — the wordmark
 * SVG is path-identical so all three cart surfaces look the same.
 *
 * Inline SVG (not a PNG/CDN asset) so it inherits the host's theme
 * colour and prints crisp at retina. Stripe's brand guidelines
 * explicitly permit the "Powered by Stripe" lockup on integrator
 * surfaces with no further trademark notice.
 */
function stripeBadgeHtml(): string {
  return `
    <div class="cobuntu-cart-stripe-badge" role="note" aria-label="Secure checkout powered by Stripe. PCI-DSS Level 1 compliant.">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2"></rect>
        <path d="M7 11V7a5 5 0 0110 0v4"></path>
      </svg>
      <span>Secure checkout · Powered by</span>
      <svg role="img" aria-label="Stripe" viewBox="0 0 60 25" height="10" fill="currentColor">
        <path d="M59.5 14.6c0-4.3-2.1-7.7-6.1-7.7s-6.4 3.4-6.4 7.7c0 5.1 2.9 7.6 7 7.6 2 0 3.5-.5 4.7-1.1v-3.4c-1.2.6-2.5.9-4.2.9-1.7 0-3.1-.6-3.3-2.6h8.3v-1.4zm-8.4-1.6c0-1.9 1.2-2.7 2.2-2.7 1.1 0 2.2.8 2.2 2.7h-4.4zM40.5 6.9c-1.6 0-2.7.8-3.2 1.3l-.2-1.1h-3.7v19.6l4.2-.9v-4.8c.6.4 1.5 1 3 1 3 0 5.8-2.4 5.8-7.7-.1-4.8-2.8-7.4-5.9-7.4zm-1 11.4c-1 0-1.6-.4-2-.8V11c.4-.4 1.1-.8 2-.8 1.5 0 2.6 1.7 2.6 4 0 2.4-1 4.1-2.6 4.1zM27.6 5.9l4.2-.9V1.6l-4.2.9v3.4zm0 1.3h4.2v14.7h-4.2zM23.1 8.4l-.3-1.2h-3.6v14.7h4.2v-10c1-1.3 2.7-1 3.2-.9v-3.8c-.5-.2-2.4-.6-3.5 1.2zm-8.4-4.8l-4.1.9-.1 13.4c0 2.5 1.9 4.3 4.4 4.3 1.4 0 2.4-.3 3-.6v-3.4c-.5.2-3.3 1-3.3-1.6V10.8h3.3V7.2h-3.3V3.6zM4.2 11.3c0-.7.5-1 1.4-1 1.2 0 2.7.4 3.9 1V7.4c-1.3-.5-2.7-.7-3.9-.7C2.4 6.7 0 8.4 0 11.2c0 4.4 6 3.7 6 5.6 0 .8-.7 1.1-1.6 1.1-1.3 0-3-.5-4.4-1.3v4c1.5.7 3.1 1 4.4 1 3.4 0 5.9-1.7 5.9-4.5 0-4.7-6.1-3.9-6.1-5.8z"/>
      </svg>
    </div>
  `;
}

// ─── Styles (injected once) ───────────────────────────────────────

const CSS = `
.cobuntu-cart-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  /*
   * CSS-var-driven chrome so the cart icon matches messages,
   * notifications, and auth on landings that set --cobuntu-border
   * et al. Important: we intentionally DO NOT honour
   * --cobuntu-padding here. That var is set on landings like Bela
   * for the button-shaped widgets (auth pill = 6px 16px), and
   * under the platform-default box-sizing border-box it would eat
   * the fixed 36 x 36 box and crop the cart SVG to a sliver
   * (regression that erased Bela cart icon 2026-05-26). The icon
   * trigger is always 36 x 36 with zero padding, matching the
   * messages and notifications --icon variants.
   */
  background: var(--cobuntu-bg, transparent);
  border: var(--cobuntu-border, 0);
  border-radius: var(--cobuntu-radius, 0);
  padding: 0;
  box-sizing: border-box;
  color: var(--cobuntu-cart-icon-color, var(--cobuntu-color, currentColor));
  cursor: pointer;
  font: inherit;
}
.cobuntu-cart-trigger:hover { background: var(--cobuntu-hover-bg, color-mix(in srgb, currentColor 10%, transparent)); border-radius: 9999px; }
.cobuntu-cart-trigger { transition: background 0.15s ease; }
.cobuntu-cart-trigger__icon { transition: opacity 0.15s ease; }
.cobuntu-cart-trigger__badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--cobuntu-cart-badge-bg, #d4af37);
  color: var(--cobuntu-cart-badge-text, #000);
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  pointer-events: none;
  box-sizing: border-box;
}
.cobuntu-cart-trigger__badge[hidden] { display: none; }

.cobuntu-cart-backdrop {
  position: fixed;
  inset: 0;
  background: var(--cobuntu-backdrop, rgba(0, 0, 0, 0.5));
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 2147483646;
}
.cobuntu-cart-backdrop--open {
  opacity: 1;
  pointer-events: auto;
}

.cobuntu-cart-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(420px, 100vw);
  background: var(--cobuntu-cart-drawer-bg, #fff);
  color: var(--cobuntu-cart-drawer-text, #0a0a0a);
  transform: translateX(100%);
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 2147483647;
  display: flex;
  flex-direction: column;
  font-family: var(--cobuntu-cart-font, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif);
  /* Match the account + notifications drawers: rounded on the inner (left)
     edge + the same soft left-cast shadow, so all three side drawers read as
     one family. */
  overflow: hidden;
  box-shadow: -20px 0 50px -12px rgba(0, 0, 0, 0.25);
  outline: none;
}
/* Mobile: full-screen drawer. The 420px max-width plus the box-shadow
   look fine on tablets and up but cramped on phones where a sliver of
   landing peeks through the right edge. Below 640px we expand to the
   full viewport width and drop the side shadow so the drawer reads
   as its own page rather than a popover. */
@media (max-width: 639px) {
  .cobuntu-cart-drawer {
    width: 100vw;
    box-shadow: none;
    /* Full-bleed sheet on phones — drop the inner-edge radius so the corners
       don't read as a rendering artefact. */
    border-radius: 0;
  }
}
.cobuntu-cart-drawer--open { transform: translateX(0); }

.cobuntu-cart-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--cobuntu-drawer-border, color-mix(in srgb, currentColor 9%, transparent));
}
.cobuntu-cart-drawer__title {
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}
.cobuntu-cart-drawer__count {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 9999px;
  background: var(--cobuntu-cart-badge-bg, #d4af37);
  color: var(--cobuntu-cart-badge-text, #000);
}
/* Circular muted close, matching the account + notifications drawers. */
.cobuntu-cart-drawer__close {
  background: color-mix(in srgb, currentColor 6%, transparent);
  border: 0;
  cursor: pointer;
  color: inherit;
  width: 34px;
  height: 34px;
  border-radius: 9999px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s ease;
}
.cobuntu-cart-drawer__close:hover { background: color-mix(in srgb, currentColor 12%, transparent); }
.cobuntu-cart-drawer__close-line {
  display: block;
  width: 15px;
  height: 1.5px;
  border-radius: 2px;
  background: currentColor;
  transition: transform 0.3s ease;
  transform-origin: center;
}
.cobuntu-cart-drawer__close-line--top {
  transform: translateY(3.25px) rotate(45deg);
}
.cobuntu-cart-drawer__close-line--bottom {
  transform: translateY(-3.25px) rotate(-45deg);
}

.cobuntu-cart-drawer__list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 16px;
}
.cobuntu-cart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 56px 24px;
  margin: 0;
}
.cobuntu-cart-empty__icon {
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, currentColor 6%, transparent);
  opacity: 0.55;
  margin-bottom: 16px;
}
.cobuntu-cart-empty__title { font-size: 15px; font-weight: 600; margin: 0; }
.cobuntu-cart-empty__hint {
  font-size: 13px;
  opacity: 0.5;
  margin: 6px 0 0;
  max-width: 220px;
  line-height: 1.5;
}
.cobuntu-cart-empty__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 220px;
  margin-top: 20px;
}
.cobuntu-cart-empty__btn {
  display: block;
  width: 100%;
  padding: 11px 16px;
  border-radius: var(--cobuntu-button-radius, 12px);
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.15s ease, background 0.15s ease;
}
.cobuntu-cart-empty__btn--primary {
  background: var(--cobuntu-cart-primary-bg, #0a0a0a);
  color: var(--cobuntu-cart-primary-text, #fff);
}
.cobuntu-cart-empty__btn--primary:hover { opacity: 0.9; }
.cobuntu-cart-empty__btn--muted {
  background: color-mix(in srgb, currentColor 8%, transparent);
  color: var(--cobuntu-cart-drawer-text, #0a0a0a);
}
.cobuntu-cart-empty__btn--muted:hover { background: color-mix(in srgb, currentColor 14%, transparent); }
.cobuntu-cart-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--cobuntu-drawer-border, color-mix(in srgb, currentColor 10%, transparent));
}
.cobuntu-cart-item:last-child { border-bottom: 0; }
.cobuntu-cart-item__image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  background: var(--cobuntu-drawer-hover, color-mix(in srgb, currentColor 8%, transparent));
  flex-shrink: 0;
  display: block;
}
/* Seeded artwork rather than an empty grey box. The inline background-image
   comes from placeholderGradient(); these are the bits that do not vary. */
.cobuntu-cart-item__image--placeholder {
  background-size: cover;
  background-position: center;
}
.cobuntu-cart-item__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}
.cobuntu-cart-item__name {
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.cobuntu-cart-item__meta {
  font-size: 12px;
  opacity: 0.6;
}
.cobuntu-cart-item__price {
  font-size: 13px;
  font-weight: 600;
}
.cobuntu-cart-qty {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 6px;
  border: 1px solid color-mix(in srgb, currentColor 16%, transparent);
  border-radius: 8px;
  overflow: hidden;
}
.cobuntu-cart-qty__btn {
  appearance: none;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 15px;
  line-height: 1;
  /* 28px is below the 44px touch target used elsewhere, but this control sits
     inside a dense list row; the remove button remains the large target. */
  min-width: 28px;
  height: 28px;
  cursor: pointer;
  opacity: 0.75;
}
.cobuntu-cart-qty__btn:hover { opacity: 1; background: color-mix(in srgb, currentColor 7%, transparent); }
.cobuntu-cart-qty__btn:disabled { opacity: 0.35; cursor: wait; }
.cobuntu-cart-qty__value {
  min-width: 22px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
/* A real target, matching the app's cart row: 44px, red, centred on the line.
   It was a 20px "x" glyph with 4px of padding - roughly a 28x20 hit area, well
   under the 44px minimum, and styled as text rather than as the destructive
   action it is. */
.cobuntu-cart-item__remove {
  flex: 0 0 auto;
  align-self: center;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
  transition: background 0.15s ease, transform 0.1s ease;
}
.cobuntu-cart-item__remove:hover { background: rgba(239, 68, 68, 0.16); }
.cobuntu-cart-item__remove:active { transform: scale(0.95); }

/* The tier, on its own line under the name - it used to be glued into the
   name field with a " - ", where nothing could style it or tell it from a
   product whose real name contains a dash. */
.cobuntu-cart-item__tier {
  font-size: 11px;
  opacity: 0.7;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cobuntu-cart-footer {
  padding: 16px;
  border-top: 1px solid var(--cobuntu-drawer-border, color-mix(in srgb, currentColor 9%, transparent));
  display: flex;
  flex-direction: column;
  gap: 12px;
}
/* Total line — reads as the amount you're about to pay, so it carries more
   weight than a plain row (the label stays muted, the figure is prominent). */
.cobuntu-cart-footer__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 15px;
}
.cobuntu-cart-footer__row-label { font-weight: 500; opacity: 0.65; }
/* Supporting lines (savings, shipping) sit below the figure that matters, so
   they are quieter than the total itself. */
.cobuntu-cart-footer__row--muted { font-size: 13px; opacity: 0.8; }
.cobuntu-cart-footer__row-save { font-weight: 600; color: #15803d; }
/* A refusal is the server declining to price this cart at all. It replaces the
   confident total that used to sit here for an order that would be rejected at
   payment. */
.cobuntu-cart-refusal {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  padding: 10px 12px;
  border-radius: 10px;
  background: color-mix(in srgb, #dc2626 10%, transparent);
}
.cobuntu-cart-footer__row-value { font-weight: 700; font-size: 17px; }
.cobuntu-cart-checkout {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 16px;
  background: var(--cobuntu-cart-primary-bg, #0a0a0a);
  color: var(--cobuntu-cart-primary-text, #fff);
  border: 0;
  border-radius: 12px;
  font-size: 14.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  font: inherit;
  box-shadow: 0 6px 18px -8px color-mix(in srgb, var(--cobuntu-cart-primary-bg, #0a0a0a) 70%, transparent);
  transition: box-shadow 0.15s ease, opacity 0.15s ease;
}
.cobuntu-cart-checkout svg { flex-shrink: 0; }
.cobuntu-cart-checkout:disabled { opacity: 0.6; cursor: wait; box-shadow: none; }
.cobuntu-cart-checkout:hover:not(:disabled) {
  opacity: 0.94;
  box-shadow: 0 10px 24px -8px color-mix(in srgb, var(--cobuntu-cart-primary-bg, #0a0a0a) 78%, transparent);
}

.cobuntu-cart-error {
  margin: 0;
  font-size: 12px;
  color: var(--cobuntu-danger, #c63b3b);
  text-align: center;
}

.cobuntu-cart-stripe-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0.55;
  font-size: 11px;
  line-height: 1;
}
.cobuntu-cart-stripe-badge svg {
  display: inline-block;
}
`;

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

// ─── Mount management (initial scan + late-arriving DOM) ──────────

function mountAll(): void {
  document
    .querySelectorAll<HTMLElement>(`${MOUNT_SELECTOR}:not([${MOUNTED_ATTR}])`)
    .forEach(renderTrigger);
}

let observer: MutationObserver | null = null;

function watchDom(): void {
  if (observer) return;
  observer = new MutationObserver(mountAll);
  observer.observe(document.body, { childList: true, subtree: true });
}

// ─── Boot ─────────────────────────────────────────────────────────

function init(): void {
  injectStyles();
  mountAll();
  watchDom();

  /*
   * CROSS-ORIGIN: wire the server cart and load it.
   *
   * Config resolves the community and its publishable key from the host, and
   * both of those calls are already unauthenticated and cross-origin-safe.
   * Hydration is fire-and-forget: the badge renders 0 until it lands, then
   * onChange re-renders. A failure leaves the cache alone rather than showing
   * an empty cart, because "your basket is empty" is a worse lie than a stale
   * count.
   */
  if (isCrossOrigin()) {
    void loadConfig()
      .then((config) => {
        configureServerCart({
          apiUrl: API_URL,
          communityTag: config.communityTag,
          publishableKey: config.publishableKey,
          onChange: () => {
            updateAllBadges();
            if (drawerEl?.classList.contains("cobuntu-cart-drawer--open")) renderDrawer();
          },
        });
        return primeServerCart();
      })
      .catch(() => { /* offline or misconfigured host — the widget still renders */ });
  }
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) {
      updateAllBadges();
      if (drawerEl?.classList.contains("cobuntu-cart-drawer--open")) {
        renderDrawer();
      }
    }
  });
  // Same-tab updates: the `storage` event above only fires in OTHER tabs, so
  // when the community-app's React cart (same tab) adds/removes an item it
  // dispatches `cobuntu:cart-changed` — refresh the badge + open drawer on it.
  window.addEventListener("cobuntu:cart-changed", () => {
    updateAllBadges();
    if (drawerEl?.classList.contains("cobuntu-cart-drawer--open")) {
      renderDrawer();
    }
  });
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}

// ─── Public API (window.CobuntuCart) ──────────────────────────────

declare global {
  interface Window {
    CobuntuCart?: {
      open: () => void;
      close: () => void;
      count: () => number;
      refresh: () => void;
    };
  }
}

if (typeof window !== "undefined") {
  window.CobuntuCart = {
    open: openDrawer,
    close: closeDrawer,
    count: cartCount,
    refresh: () => {
      mountAll();
      updateAllBadges();
      if (drawerEl?.classList.contains("cobuntu-cart-drawer--open")) renderDrawer();
    },
  };
}

// Exports for tests — bundler-stripped via esbuild `globalName`.
export const __test = {
  readCart,
  writeCart,
  cartCount,
  isAuthenticated,
  escapeHtml,
  formatPrice,
  formatDate,
  STORAGE_KEY,
  itemHtml,
};
